import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Box, Button, Checkbox, Link, Stack, Typography, useTheme } from '@mui/material';
import { TERMS_AND_CONDITIONS_VERSION } from '@repo/legal';

import { AuthErrorBoundary, AuthErrorMessage } from 'components/auth';
import { Input } from 'components/UI/Input';
import { createApiCall } from 'services';
import { LegalDocumentDrawer } from 'components/UI/LegalDocumentDrawer';
import { CompanyContext } from 'contexts/companyContext';
import { TERMS_URL } from 'constants/smsConsent';
import { useSendMagicAuthCode } from 'hooks/api/useWorkOSAuth';
import { ROUTES } from 'routes';
import { emailRex } from 'settings';

import { OnboardingAuthPanel } from '../components/OnboardingAuthPanel';

// TODO(VRG-173): promote to categorized app component library / @repo/ui once
// apps/app gets its own component library (VERA-837 build-decision note).

/**
 * VERA-837 — Owner self-serve create-account.
 *
 * `apps/app` is embedded at a company's install; this is the ONLY
 * "create account" surface for that company's Owners — VERA is B2B
 * (companies are onboarded via sales), so there is no company-creation /
 * plan / Stripe flow here. Collects Full name + Email, sends a WorkOS
 * magic-auth OTP, then verifies it via the shared `OnboardingAuthPanel`
 * (also used by the invite-link variant below and by Claim-Ownership
 * Sign-Up), threading the typed name through to
 * `CustomerWorkOSCallbackUseCase` so it lands on the new owner record
 * (OTP magic-auth has no WorkOS profile name to fall back on).
 *
 * Still supports the `create-account-invite` email link (VERA-843 —
 * `create-account-invite` template, `SendCreateAccountInviteUseCase`):
 * `email` (prefilled, still editable) and `companyId` query params, same
 * as before.
 */
const CreateAccountEmail = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const { companyId: contextCompanyId, setCompanyId } = useContext(CompanyContext);

  const emailParam = searchParams.get('email') ?? '';
  const companyIdParam = searchParams.get('companyId') ?? undefined;

  // Override the app-level company resolution (subdomain / embed key /
  // fallback placeholder — see App.tsx) with the company carried on the
  // invite link, so the resulting owner-company link matches the inviting
  // company rather than whatever App.tsx resolved on mount.
  useEffect(() => {
    if (companyIdParam) {
      setCompanyId(companyIdParam);
    }
  }, [companyIdParam, setCompanyId]);

  const companyId = companyIdParam ?? contextCompanyId;

  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [usernameStatus, setUsernameStatus] = useState<
    { kind: 'checking' | 'available' | 'taken' | 'invalid'; message: string } | null
  >(null);
  const [email, setEmail] = useState(emailParam);
  const [error, setError] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsDrawerOpen, setTermsDrawerOpen] = useState(false);

  const [isSendingCode, setIsSendingCode] = useState(false);
  const sendCodeInFlightRef = useRef(false);

  const sendMagicAuthCode = useSendMagicAuthCode();

  // Full name -> first/last split: first whitespace-delimited token is the
  // first name, everything after is the last name (empty string for a
  // single-word name). Threaded through to verify (Task 2) so a brand-new
  // owner is provisioned with a real name.
  const { firstName, lastName } = useMemo(() => {
    const trimmed = fullName.trim();
    if (!trimmed) return { firstName: '', lastName: '' };
    const [first, ...rest] = trimmed.split(/\s+/);
    return { firstName: first, lastName: rest.join(' ') };
  }, [fullName]);

  /**
   * VRG-357 — the handle's character set, applied AS YOU TYPE rather than rejected on
   * submit. A handle is typed once and lived with; telling someone their spaces and
   * capitals are invalid after they press the button is a worse trade than quietly
   * shaping the input while they watch it happen.
   *
   * Lowercase because the uniqueness index is `lower(username)` (VERA-837): allowing
   * mixed case in the field would let someone type `Alice`, be told it is free, and
   * then collide with an existing `alice`.
   */
  const normalizeHandle = (raw: string) =>
    raw.trim().toLowerCase().replace(/^@+/, '').replace(/[^a-z0-9._]/g, '').slice(0, 30);

  // Debounced availability check. The endpoint (VRG-135) is an existence oracle for
  // HANDLES specifically, which is the deliberate trade: a handle is a public
  // identifier its owner publishes, unlike the email address this replaces as the
  // lookup key.
  useEffect(() => {
    const handle = username.trim();
    if (!handle) {
      setUsernameStatus(null);
      return;
    }
    if (handle.length < 3) {
      setUsernameStatus({ kind: 'invalid', message: 'At least 3 characters.' });
      return;
    }
    setUsernameStatus({ kind: 'checking', message: 'Checking…' });
    const t = setTimeout(async () => {
      try {
        const res: any = await createApiCall({
          method: 'GET',
          // VRG-357: the PUBLIC check (`/auth/...`), not the authenticated one on
          // `/auth/customer/...`. This page runs before an account exists, so the
          // authenticated endpoint 401s on every keystroke — and its semantics are
          // wrong here anyway: it answers `available: isSelf` so that someone editing
          // their profile can re-save their own handle without being told it is taken.
          // There is no "self" during signup.
          url: `/auth/username-available?username=${encodeURIComponent(handle)}`,
        });
        const available = res?.data?.available ?? res?.available;
        setUsernameStatus(
          available
            ? { kind: 'available', message: `@${handle} is available.` }
            : { kind: 'taken', message: `@${handle} is already taken.` },
        );
      } catch {
        // Never block signup on a availability-check failure — the API enforces
        // uniqueness on write regardless, so the worst case is an error at submit
        // rather than a person unable to proceed because a check timed out.
        setUsernameStatus(null);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [username]);

  const handleSubmit = async () => {
    if (sendCodeInFlightRef.current) return;

    if (!fullName.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (!username.trim() || username.trim().length < 3) {
      setError('Please choose a username of at least 3 characters.');
      return;
    }
    if (usernameStatus?.kind === 'taken') {
      setError('That username is taken. Please choose another.');
      return;
    }
    // VERA-837: validate the FORMAT, not just presence — mirrors
    // `SignInBanner.handleSendCode` (a bare `!email` check let malformed
    // input reach the API, which 400s, and the raw backend message rendered
    // verbatim as "Bad Request").
    if (!emailRex.test(email.trim())) {
      setError('Enter a valid email address.');
      return;
    }
    // VERA-837: never disable the submit button for this — clicking with the
    // box unchecked surfaces the error inline instead (standing rule for
    // this epic), matching the pattern above for name/email.
    if (!acceptedTerms) {
      setError('Please accept the terms to continue.');
      return;
    }

    sendCodeInFlightRef.current = true;
    setIsSendingCode(true);
    setError(null);
    try {
      await sendMagicAuthCode.mutateAsync({ email: email.trim(), companyId });
      setCodeSent(true);
    } catch (err: any) {
      // Never surface a bare HTTP status ("Bad Request", "Forbidden") as copy.
      const backendMsg: string | undefined = err?.response?.data?.message;
      const isRawStatus =
        !backendMsg || /^(bad request|unauthorized|forbidden|not found|internal server error)$/i.test(backendMsg.trim());
      setError(isRawStatus ? 'Failed to send the code. Please try again.' : backendMsg);
    } finally {
      sendCodeInFlightRef.current = false;
      setIsSendingCode(false);
    }
  };

  return (
    <AuthErrorBoundary>
      <div className='flex flex-col items-center pt-[20vh] pb-8 px-4 bg-background min-h-screen w-full'>
        <div className='w-full max-w-[400px]'>
          <h2 className='mb-4 text-[24px] leading-[32px] font-[400]'>Create account</h2>

          {codeSent ? (
            <OnboardingAuthPanel
              email={email.trim()}
              companyId={companyId}
              firstName={firstName}
              lastName={lastName}
              username={username.trim() || undefined}
              initialCodeSent
              acceptedTerms={acceptedTerms}
              termsVersion={TERMS_AND_CONDITIONS_VERSION}
              onVerified={() => navigate(ROUTES.CERTIFICATES)}
            />
          ) : (
            <Stack sx={{ width: '100%' }} gap={2}>
              {/*
                VERA-837: Enter submits the primary action. apps/app renders in
                a shadow DOM where <form onSubmit> does NOT fire, so every
                surface wires Enter explicitly on its fields (SignInBanner and
                ForgotPassword already do; the onboarding surfaces had none).
              */}
              <Input
                value={fullName}
                onChange={setFullName}
                placeholder='Full name'
                sx={{ '& input': { paddingLeft: '8px' } }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
              />
              {/* VRG-357 — choose the handle at signup, not later.
                  Recipient lookup is handle-only now (matching on email was an
                  enumeration oracle), so an account with an auto-generated handle
                  like `johnd6ab` cannot be addressed by anyone: it is unguessable and
                  its owner has no reason to know it exists. Two "John Doe" accounts
                  separated only by that string is how a gift reached the wrong person
                  during VERA-304's verification. */}
              <Input
                value={username}
                onChange={(v: string) => setUsername(normalizeHandle(v))}
                placeholder='Username'
                startAdornment={<span style={{ color: '#7A7A7A' }}>@</span>}
                sx={{ '& input': { paddingLeft: '4px' } }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
              />
              {usernameStatus && (
                <Typography
                  variant='body2'
                  sx={theme => ({
                    mt: -1,
                    pl: 1,
                    lineHeight: 1.5,
                    color:
                      usernameStatus.kind === 'taken'
                        ? theme.palette.error.main
                        : theme.palette.text.secondary,
                  })}
                >
                  {usernameStatus.message}
                </Typography>
              )}
              <Input
                type='email'
                value={email}
                onChange={setEmail}
                placeholder='Email'
                sx={{ '& input': { paddingLeft: '8px' } }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
              />
              {/*
                VERA-837: consent checkbox, mirrors the pattern established by
                PhoneVerificationDrawer's SMS-consent checkbox (bare MUI
                Checkbox + Typography — no shared @repo/ui CheckBox fits: it
                only takes a plain string `label` and requires an RHF
                `register`, but the label here needs an embedded clickable
                link and this form has no RHF).
              */}
              <Stack direction='row' spacing={2} alignItems='center'>
                <Checkbox
                  checked={acceptedTerms}
                  onChange={(_, next) => {
                    setAcceptedTerms(next);
                    setError(null);
                  }}
                  // VERA-837: matches the Enter-to-submit wiring on the inputs
                  // above — a native checkbox doesn't submit on Enter (only
                  // Space toggles it), so a user who tabs to the checkbox and
                  // presses Enter would otherwise hit a dead key.
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                  sx={{
                    p: 0,
                    // Unchecked comes from `settings/mui/MuiCheckbox.ts` (`text.primary`).
                    // It was set by hand here only to work around a base default of
                    // `primary.contrastText`, i.e. white on this white form. Checked
                    // keeps the brand accent — this is a consent control.
                    '&.Mui-checked': { color: theme.palette.primary.main },
                  }}
                  inputProps={{ 'aria-label': 'Accept the Terms and Conditions' }}
                />
                <Typography variant='body1' sx={{ color: theme.palette.text.primary, lineHeight: 1.5 }}>
                  I accept the{' '}
                  <Link
                    component='button'
                    type='button'
                    onClick={() => setTermsDrawerOpen(true)}
                    sx={{ fontWeight: 500, fontSize: 'inherit', verticalAlign: 'baseline' }}
                    color='inherit'
                  >
                    Terms and Conditions
                  </Link>
                </Typography>
              </Stack>
              <AuthErrorMessage message={error} />
              <Button
                variant='contained'
                fullWidth
                onClick={handleSubmit}
                disabled={isSendingCode}
                sx={{ paddingX: 2 }}
              >
                {isSendingCode ? 'Sending...' : 'Create account'}
              </Button>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant='body1' color='text.secondary'>
                  Already have an account?{' '}
                  <Typography
                    component='span'
                    variant='body1'
                    onClick={() => navigate(ROUTES.SIGN_IN)}
                    sx={{
                      color: 'text.primary',
                      fontWeight: 500,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    Log in
                  </Typography>
                </Typography>
              </Box>
            </Stack>
          )}
        </div>
      </div>
      <LegalDocumentDrawer
        open={termsDrawerOpen}
        onClose={() => setTermsDrawerOpen(false)}
        title='Terms and Conditions'
        url={TERMS_URL}
      />
    </AuthErrorBoundary>
  );
};

export default CreateAccountEmail;
